// frontend/src/theme/create-theme.js
import { createTheme as createMuiTheme } from '@mui/material/styles';
import { GLASS } from '@caro/shared/theme';
import { applyPreset } from './theme-color-presets';
import { plumTheme } from './plugins/plum-theme';
import { goldTheme } from './plugins/gold-theme';

// Create theme with settings including accessibility options
export const createTheme = (language = 'en', settings = {}) => {
  const {
    direction = 'ltr',
    themeMode = 'light',
    themeColor = 'plum', // Default to plum
    typography = 'Nunito, sans-serif',
    textSize = 'small',
    contrastMode = false,
    reducedMotion = false,
    density = 'comfortable',
  } = settings;

  const compact = density === 'compact';

  // Base font size based on text size setting
  const baseFontSize = {
    small: 14,
    medium: 16,
    large: 18,
    xlarge: 20,
  }[textSize] || 14;

  // Select brand theme based on color
  let brandTheme = {};
  if (themeColor === 'plum') {
    brandTheme = plumTheme;
  } else if (themeColor === 'gold') {
    brandTheme = goldTheme;
  }

  // ── DE DRIE GLASWAARDEN VOOR DEZE MODUS ──────────────────────────────────
  //
  // Eén keer uitgerekend en daarna hergebruikt, zodat de balk, het zijmenu en de dialogen
  // gegarandeerd hetzelfde glas dragen. Ze uit elkaar laten lopen is precies hoe er eerder
  // drie verschillende blur-waarden in deze codebase belandden.
  const isDark = themeMode === 'dark';
  const glassBg = isDark ? GLASS.backgroundStrongDark : GLASS.backgroundStrong;
  const glassBgLight = isDark ? GLASS.backgroundDark : GLASS.background;
  const glassBorder = isDark ? GLASS.borderDark : GLASS.border;
  const glassShadow = isDark ? GLASS.shadowDark : GLASS.shadow;

  // Create base theme
  let theme = createMuiTheme({
    direction,
    palette: {
      mode: themeMode,
      ...(brandTheme.palette || {}),
      ...(themeMode === 'dark' && {
        background: {
          default: contrastMode ? '#141a21' : '#121212',
          paper: contrastMode ? '#1c252e' : '#1e1e1e',
        },
        text: {
          primary: contrastMode ? '#FFFFFF' : '#c7cdd1',
          secondary: contrastMode ? '#c7cdd1' : '#a0a0a0',
        },
      }),
    },
    typography: {
      fontFamily: typography,
      fontSize: baseFontSize,
      h1: { fontSize: baseFontSize * 2.5, fontWeight: 700 },
      h2: { fontSize: baseFontSize * 2, fontWeight: 700 },
      h3: { fontSize: baseFontSize * 1.75, fontWeight: 600 },
      h4: { fontSize: baseFontSize * 1.5, fontWeight: 600 },
      h5: { fontSize: baseFontSize * 1.25, fontWeight: 500 },
      h6: { fontSize: baseFontSize * 1.1, fontWeight: 500 },
      body1: { fontSize: baseFontSize },
      body2: { fontSize: baseFontSize * 0.875 },
      button: { fontWeight: 600, textTransform: 'none' },
    },
    shape: {
      borderRadius: 8,
    },
    customShadows: brandTheme.customShadows || {},
    gradients: brandTheme.gradients || {},
    components: {
      // ── GLAS OP NAVIGATIE EN DIALOGEN ─────────────────────────────────────
      //
      // Hier en niet in de dertig plekken die een dialoog openen: dit is de enige plaats
      // waar het één keer geregeld is, en waar niemand het per ongeluk anders doet.
      //
      // De portal kent zijn eigen licht/donker via `palette.mode`, dus hier worden de
      // CONSTANTEN gebruikt en niet de CSS-variabelen: die laatste kantelen mee met
      // `[data-theme]` van de bewonersapp en zouden hier de verkeerde kant op kunnen.
      //
      // Dialogen krijgen de DICHTE variant. Een formulier of een tabel op te veel
      // doorschijn leest slecht, en leesbaarheid weegt zwaarder dan het effect.
      MuiButton: {
        styleOverrides: {
          root: {
            // Increase padding for better touch targets
            padding: textSize === 'large' || textSize === 'xlarge' 
              ? '8px 20px' 
              : undefined,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
          contained: {
            boxShadow: brandTheme.customShadows?.button || 'none',
            '&:hover': {
              boxShadow: brandTheme.customShadows?.button || 'none',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: brandTheme.customShadows?.card || 'none',
            borderRadius: 12,
            position: 'relative',
            zIndex: 0,
            transition: 'transform 180ms ease, box-shadow 180ms ease',
          },
        },
        variants: [
          {
            // Opt-in interactive lift — cards that use `<Card variant="interactive">`
            // rise on hover. Non-interactive cards keep their default look.
            props: { variant: 'interactive' },
            style: ({ theme: t }) => ({
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: t.shadows[4],
              },
            }),
          },
        ],
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: 8,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: glassBg,
            backdropFilter: GLASS.filter,
            // Zonder voorvoegsel geen glas maar een doorzichtig vlak op oudere WebKit.
            WebkitBackdropFilter: GLASS.filter,
            border: `1px solid ${glassBorder}`,
            boxShadow: brandTheme.customShadows?.dialog || glassShadow,
            borderRadius: 12,
          },
        },
      },
      // De balk bovenaan zweeft over de inhoud, dus die mag de LICHTE variant: je ziet
      // eronder doorschemeren wat er staat, en dat helpt bij het scrollen.
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: glassBgLight,
            backdropFilter: GLASS.filter,
            WebkitBackdropFilter: GLASS.filter,
            borderBottom: `1px solid ${glassBorder}`,
            boxShadow: 'none',
            backgroundImage: 'none',
            // De tekst kan niet meer op de merkkleur leunen nu de balk doorschijnend is.
            color: isDark ? '#F7FAFC' : 'rgba(0, 0, 0, 0.87)',
          },
          // ⚠ `root` ALLEEN is niet genoeg. Een AppBar staat standaard op `color="primary"`,
          // en die klasse zet de achtergrond NA `root` -- dan blijft de balk gewoon de
          // merkkleur en gebeurt er zichtbaar niets. Vandaar dat de kleurklassen hier
          // expliciet meedoen.
          colorPrimary: {
            backgroundColor: glassBgLight,
            color: isDark ? '#F7FAFC' : 'rgba(0, 0, 0, 0.87)',
          },
          colorDefault: {
            backgroundColor: glassBgLight,
          },
        },
      },
      // Het zijmenu draagt navigatie -- tekst die scherp moet blijven. Dichte variant.
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: glassBg,
            backdropFilter: GLASS.filter,
            WebkitBackdropFilter: GLASS.filter,
            borderRight: `1px solid ${glassBorder}`,
            backgroundImage: 'none',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: baseFontSize * 0.75,
            backgroundColor: themeMode === 'light' ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.8)',
            color: themeMode === 'light' ? '#fff' : '#000',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            fontSize: baseFontSize,
          },
        },
      },
      // Touch-target comfort — raise IconButton minimum hit area on
      // smaller viewports so taps don't require pixel-perfect aim.
      // 40x40 on mobile (finger-friendly), auto on desktop to keep
      // existing compact layouts untouched.
      MuiIconButton: {
        styleOverrides: {
          root: {
            '@media (pointer: coarse)': {
              minWidth: 40,
              minHeight: 40,
            },
          },
          sizeSmall: {
            '@media (pointer: coarse)': {
              minWidth: 36,
              minHeight: 36,
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '@media (pointer: coarse)': {
              minHeight: 44,
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '@media (pointer: coarse)': {
              minHeight: 44,
            },
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            minWidth: 64,
          },
        },
      },
      // Row density — flip every Table into small spacing when the
      // user picks compact mode. Individual tables can still pass an
      // explicit `size` to override.
      MuiTable: {
        defaultProps: {
          size: compact ? 'small' : 'medium',
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            paddingTop: compact ? 4 : undefined,
            paddingBottom: compact ? 4 : undefined,
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            paddingTop: compact ? 2 : undefined,
            paddingBottom: compact ? 2 : undefined,
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            // Enhance link underlines for better visibility
            textDecorationThickness: contrastMode ? '2px' : undefined,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            // Prevent iOS from zooming form inputs when focused — keeps
            // editors stable across orientations.
            WebkitTextSizeAdjust: '100%',
          },
          body: {
            // Apply reduced motion if enabled
            ...(reducedMotion && {
              '& *': {
                animationDuration: '0.01ms !important',
                animationIterationCount: '1 !important',
                transitionDuration: '0.01ms !important',
                scrollBehavior: 'auto !important',
              },
            }),
            // Respect iOS home indicator / notch when the body is the
            // scroll container. Individual surfaces (drawers, bars)
            // still honour safe-area via their own CSS.
            overscrollBehavior: 'none',
          },
          // Visible focus ring using the brand primary color — shows
          // only on keyboard navigation (`:focus-visible`), so mouse
          // users don't see outlines after clicking.
          '*:focus-visible': {
            outline: (brandTheme.palette?.primary?.main
              ? `2px solid ${brandTheme.palette.primary.main}`
              : '2px solid #9A3F69'),
            outlineOffset: '2px',
            borderRadius: '4px',
          },
          // Thin, brand-friendly scrollbars. Honours the user's dark/light
          // mode and keeps the classic scrollbar feel on Firefox via
          // `scrollbar-*` properties.
          '*::-webkit-scrollbar': {
            width: '10px',
            height: '10px',
          },
          '*::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: themeMode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)',
            borderRadius: '8px',
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            backgroundColor: brandTheme.palette?.primary?.main || '#9A3F69',
          },
          'html': {
            scrollbarColor: `${themeMode === 'dark' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)'} transparent`,
            scrollbarWidth: 'thin',
          },
          // Notistack mobile lift — keep toasts clear of the fixed
          // bottom nav (56px) + iOS home indicator.
          '.caro-snackbar-root.SnackbarContainer-bottom': {
            bottom: 'calc(56px + env(safe-area-inset-bottom, 0px) + 8px) !important',
          },
          '@media (min-width: 900px)': {
            '.caro-snackbar-root.SnackbarContainer-bottom': {
              bottom: '16px !important',
            },
          },
        },
      },
    },
  });

  // Apply color preset if selected but not already applied via brandTheme
  if (themeColor !== 'plum' && themeColor !== 'gold') {
    theme = applyPreset(theme, themeColor);
  }

  return theme;
};

export default createTheme;